import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { jwtDecode } from 'jwt-decode';
import { acceptCameraInvite } from '../graphql/mutations';
import { GraphQLQuery } from '@aws-amplify/api';
import { AcceptCameraInviteMutation } from '../API';
import './InviteHandler.css';

const client = generateClient();

interface InviteHandlerProps {
  token: string | null;
  user: any;
  onError: () => void;
}

interface InviteData {
  cameraId: string;
  cameraName: string;
  inviterUserId: string;
  inviterName: string;
  type: string;
}

const InviteHandler: React.FC<InviteHandlerProps> = ({
  token,
  user,
  onError
}) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);

  useEffect(() => {
    if (!token || !user) return;

    const processInvite = async () => {
      try {
        console.log('🔗 [WEB] Processing invite for user:', user.username);
        
        // First, decode the JWT to show invite info (don't verify - let Lambda do that)
        const decoded = jwtDecode<InviteData>(token);
        
        if (!decoded || decoded.type !== 'camera_invite') {
          throw new Error('Invalid invite token');
        }
        
        setInviteData(decoded);
        setMessage(`Joining ${decoded.cameraName}...`);


        // Call the acceptCameraInvite mutation
        const result = await client.graphql<GraphQLQuery<AcceptCameraInviteMutation>>({
          query: acceptCameraInvite,
          variables: {
            token,
            userId: user.username,
          },
        });

        const response = result.data.acceptCameraInvite;
        
        if (response?.success) {
          console.log('✅ [WEB] Successfully accepted invite');
          setStatus('success');
          setMessage(`Welcome to ${response.cameraName}! Redirecting to App Store...`);

          // Redirect directly to App Store after a brief moment
          setTimeout(() => {
            window.location.href = 'https://apps.apple.com/us/app/phomocam/id6751013049';
          }, 2000);
        } else {
          throw new Error(response?.message || 'Failed to accept invite');
        }
        
      } catch (error: any) {
        console.error('❌ [WEB] Failed to process invite:', error);
        
        setStatus('error');
        
        let errorMessage = 'Failed to process invite';
        if (error.message?.includes('expired')) {
          errorMessage = 'This invite link has expired';
        } else if (error.message?.includes('Invalid')) {
          errorMessage = 'This invite link is invalid';
        } else if (error.message?.includes('already a member')) {
          errorMessage = 'You are already a member of this event';
        } else if (error.errors && error.errors.length > 0) {
          errorMessage = error.errors[0].message || errorMessage;
        }
        
        setMessage(errorMessage + ` (Error: ${error.message})`);
        setTimeout(onError, 5000); // Give more time to read the error
      }
    };

    processInvite();
  }, [token, user, onError]);

  return (
    <div className="invite-handler-container">
      {/* Status Icon */}
      <div className="status-icon">
        {status === 'processing' && '⏳'}
        {status === 'success' && '✅'}
        {status === 'error' && '❌'}
      </div>

      {/* Event Info */}
      {inviteData && (
        <div className="event-info-card">
          <h3 className="event-name">
            📸 {inviteData.cameraName}
          </h3>
          <p className="event-inviter">
            Invited by {inviteData.inviterName}
          </p>
        </div>
      )}

      {/* Status Message */}
      <div className="status-message-container">
        <h2 className={`status-title ${status}`}>
          {status === 'processing' && 'Processing Invite...'}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Invite Error'}
        </h2>

        <p className="status-message">
          {message}
        </p>
      </div>

      {/* Loading Spinner */}
      {status === 'processing' && (
        <div className="loading-spinner-container">
          <div className="loading-spinner" />
          <span>Adding you to the event...</span>
        </div>
      )}
    </div>
  );
};

export default InviteHandler;